<script setup lang="ts">
import { getDirectusAssetURL } from '@@/server/utils/directus-utils';

interface BlockKpisKpi {
	id: number;
	number?: string | null;
	description?: string | null;
	sort?: number | null;
	icon?: string | null;
}

interface BlockKPIsProps {
	data: {
		id?: string | number;
		background_image?: string | { id: string } | null;
		headline?: string | null;
		block_kpis?: BlockKpisKpi[] | (string | number)[];
	};
	background?: 'light' | 'dark' | null;
}

const props = defineProps<BlockKPIsProps>();
const { setAttr } = useVisualEditing();

const backgroundImageUrl = computed(() => {
	if (props.data?.background_image) {
		const imageId = typeof props.data.background_image === 'string' 
			? props.data.background_image 
			: props.data.background_image?.id;
		if (imageId) {
			return getDirectusAssetURL(imageId);
		}
	}
	return null;
});

const textColor = computed(() => {
	// Se c'è un'immagine di background, il testo è bianco
	if (backgroundImageUrl.value) {
		return 'text-white';
	}
	// Se il background è dark, il testo è bianco
	if (props.background === 'dark') {
		return 'text-white';
	}
	// Se il background è light o null, il testo è nero
	return 'text-black';
});

// Filtra e ordina i KPI validi
const sortedKPIs = computed(() => {
	if (!props.data?.block_kpis) return [];
	
	return props.data.block_kpis
		.filter((kpi): kpi is BlockKpisKpi => {
			return typeof kpi === 'object' && kpi !== null && 'id' in kpi;
		})
		.sort((a, b) => {
			const sortA = a.sort ?? 0;
			const sortB = b.sort ?? 0;
			return sortA - sortB;
		});
});
</script>

<template>
	<section
		class="relative flex justify-center max-w-full w-full py-12 md:py-16 lg:py-20"
		:style="backgroundImageUrl ? {
			backgroundImage: `url(${backgroundImageUrl})`,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			...(background === 'dark' ? { backgroundColor: '#000000' } : {}),
		} : {}"
		:data-directus="
			data?.id && backgroundImageUrl
				? setAttr({
						collection: 'block_kpis',
						item: String(data.id),
						fields: ['background_image'],
						mode: 'modal',
					})
				: undefined
		"
	>
		<!-- Overlay per migliorare la leggibilità del testo quando c'è un background -->
		<div
			v-if="backgroundImageUrl"
			class="absolute inset-0 bg-black/5 z-0"
		/>
		
		<div
			class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-16"
		>
			<!-- Headline -->
			<div
				v-if="data?.headline"
				class="text-center mb-8 md:mb-12"
				:class="textColor"
			>
				<h2 class="text-2xl md:text-3xl lg:text-4xl font-heading font-bold">
					{{ data.headline }}
				</h2>
			</div>
			
			<div
				v-if="sortedKPIs.length > 0"
				class="grid gap-8 md:gap-12"
				:class="{
					'grid-cols-1': sortedKPIs.length === 1,
					'grid-cols-1 sm:grid-cols-2': sortedKPIs.length === 2,
					'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3': sortedKPIs.length >= 3,
					'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4': sortedKPIs.length >= 4,
				}"
				:data-directus="
					data?.id
						? setAttr({
								collection: 'block_kpis',
								item: String(data.id),
								fields: ['block_kpis'],
								mode: 'modal',
							})
						: undefined
				"
			>
				<div
					v-for="kpi in sortedKPIs"
					:key="kpi.id"
					class="text-center"
					:class="textColor"
				>
					<!-- Icon -->
					<div
						v-if="kpi.icon"
						class="flex justify-center mb-4"
					>
						<span
							class="material-symbols-outlined text-accent"
							style="font-size: 3.5rem; @media (min-width: 768px) { font-size: 5rem; } @media (min-width: 1024px) { font-size: 6.5rem; }"
						>
							{{ kpi.icon }}
						</span>
					</div>
					<div
						v-if="kpi.number"
						class="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-2"
					>
						{{ kpi.number }}
					</div>
					<div
						v-if="kpi.description"
						class="text-base md:text-lg"
					>
						{{ kpi.description }}
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

